import React, { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { MAPBOX_TOKEN } from "../../config/mapbox";
import { MyDayPlan } from "./types";

const ROUTE_COLOR_MORNING = "#111111";
const ROUTE_COLOR_EVENING = "#d4af37";
const ROUTE_COLOR_DEFAULT = "#555555";

const MARKER_COLORS: Record<string, string> = {
  home: "#ffc400",
  work: "#111111",
  school: "#555555",
  shop: "#d4af37",
  extra: "#888888",
};

export interface MyDayMapProps {
  plan: MyDayPlan | null;
  propertyLon: number;
  propertyLat: number;
}

const MyDayMap: React.FC<MyDayMapProps> = ({
  plan,
  propertyLon,
  propertyLat,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const routeLayerIdsRef = useRef<string[]>([]);
  const routeSourceIdsRef = useRef<string[]>([]);

  const clearOverlays = (map: mapboxgl.Map) => {
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    for (const layerId of routeLayerIdsRef.current) {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
    }
    routeLayerIdsRef.current = [];

    for (const sourceId of routeSourceIdsRef.current) {
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    }
    routeSourceIdsRef.current = [];
  };

  const renderPlan = (map: mapboxgl.Map, nextPlan: MyDayPlan) => {
    clearOverlays(map);

    const bounds = new mapboxgl.LngLatBounds();
    bounds.extend([propertyLon, propertyLat]);

    nextPlan.stops.forEach((stop) => {
      const el = document.createElement("div");
      el.className = "myday-marker";
      el.style.cssText = `
        width: 14px; height: 14px; border-radius: 50%;
        background: ${MARKER_COLORS[stop.type] ?? "#333"};
        border: 2px solid white; box-shadow: 0 1px 4px rgba(0,0,0,0.3);
      `;
      const marker = new mapboxgl.Marker(el)
        .setLngLat([stop.longitude, stop.latitude])
        .setPopup(
          new mapboxgl.Popup({ offset: 12 }).setHTML(
            `<strong>${stop.label}</strong><br/>${stop.name}`
          )
        )
        .addTo(map);
      markersRef.current.push(marker);
      bounds.extend([stop.longitude, stop.latitude]);
    });

    nextPlan.legs.forEach((leg, idx) => {
      if (!leg.route?.geometry) return;
      const sourceId = `myday-route-${idx}`;
      const layerId = `myday-route-line-${idx}`;
      routeSourceIdsRef.current.push(sourceId);
      routeLayerIdsRef.current.push(layerId);

      map.addSource(sourceId, {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: leg.route.geometry,
        },
      });
      map.addLayer({
        id: layerId,
        type: "line",
        source: sourceId,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color":
            leg.phase === "morning"
              ? ROUTE_COLOR_MORNING
              : leg.phase === "evening"
                ? ROUTE_COLOR_EVENING
                : ROUTE_COLOR_DEFAULT,
          "line-width": 3,
          "line-opacity": 0.9,
        },
      });
    });

    map.resize();
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 0 });
      map.resize();
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || mapRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [propertyLon, propertyLat],
      zoom: 12,
      attributionControl: false,
    });
    mapRef.current = map;

    map.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      "top-right"
    );

    const resize = () => map.resize();
    const resizeObserver = new ResizeObserver(() => resize());
    resizeObserver.observe(container);

    const onLoad = () => {
      resize();
      requestAnimationFrame(resize);
      if (plan) renderPlan(map, plan);
    };

    map.on("load", onLoad);
    map.on("error", (event) => {
      console.error("MyDayMap Mapbox error:", event.error);
    });

    return () => {
      resizeObserver.disconnect();
      map.off("load", onLoad);
      clearOverlays(map);
      map.remove();
      mapRef.current = null;
    };
    // plan is read on load only; plan updates handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyLon, propertyLat]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !plan) return;

    const draw = () => renderPlan(map, plan);

    if (map.isStyleLoaded()) {
      draw();
    } else {
      map.once("load", draw);
    }
  }, [plan, propertyLon, propertyLat]);

  return (
    <div className="relative h-[420px] min-h-[420px] w-full bg-[#eee]">
      <div ref={containerRef} className="absolute inset-0 h-full w-full" />
      {!plan && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-text-grey text-sm pointer-events-none">
          Complete the form and tap Plan my day
        </div>
      )}
    </div>
  );
};

export default MyDayMap;
